import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileSignature, Search, RefreshCw, Eye, Download, Upload,
  CheckCircle2, AlertTriangle, FileText, Save, Send, ChevronRight, ChevronDown, MapPin, Share2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const API = process.env.REACT_APP_BACKEND_URL;
function authHeaders() {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const STATUS_STYLE = {
  'Нет договора': 'bg-gray-100 text-gray-600',
  'Частично заполнен': 'bg-amber-100 text-amber-800',
  'Готов': 'bg-emerald-100 text-emerald-800',
  'Отправлен': 'bg-sky-100 text-sky-800',
  'Подписан': 'bg-green-600 text-white',
  'Архив': 'bg-gray-200 text-gray-500',
};

function StatusBadge({ label }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[label] || 'bg-gray-100'}`}>{label}</span>;
}

function pointsWord(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'точка';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'точки';
  return 'точек';
}

export default function AdminContracts() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);   // open contract detail
  const [busyVat, setBusyVat] = useState({});   // per-row regen-in-flight
  const [expanded, setExpanded] = useState({}); // head user_id -> show branches
  const [bzBusy, setBzBusy] = useState({});     // per-row bitrix sync in-flight
  const [bzAll, setBzAll] = useState(false);    // bulk bitrix sync in-flight

  const toggleExpand = (uid) => setExpanded(prev => ({ ...prev, [uid]: !prev[uid] }));

  const syncBitrix = async (c) => {
    setBzBusy(prev => ({ ...prev, [c.user_id]: true }));
    try {
      const r = await axios.post(`${API}/api/admin/contracts/${c.contract_id}/sync-bitrix`, {}, { headers: authHeaders() });
      toast.success(`Отправлено в Bitrix · файлов: ${r.data.files}`);
      fetchClients(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка синхронизации с Bitrix');
    } finally {
      setBzBusy(prev => { const n = { ...prev }; delete n[c.user_id]; return n; });
    }
  };

  const syncBitrixAll = async () => {
    setBzAll(true);
    try {
      const r = await axios.post(`${API}/api/admin/contracts/sync-bitrix-all`, {}, { headers: authHeaders() });
      toast.success(`Bitrix: синхронизировано ${r.data.synced} из ${r.data.total}${r.data.failed ? ` · ошибок: ${r.data.failed}` : ''}`);
      fetchClients(false);
    } catch {
      toast.error('Ошибка массовой синхронизации с Bitrix');
    } finally {
      setBzAll(false);
    }
  };

  const fetchClients = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/contracts`, { headers: authHeaders() });
      setClients(r.data.clients || []);
    } catch (e) {
      toast.error('Не удалось загрузить список');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const toggleVat = async (c) => {
    const next = !c.is_vat_payer;
    setClients(prev => prev.map(x => x.user_id === c.user_id ? { ...x, is_vat_payer: next } : x));  // optimistic
    setBusyVat(prev => ({ ...prev, [c.user_id]: true }));
    try {
      await axios.patch(`${API}/api/admin/contracts/clients/${c.user_id}/vat`,
        { is_vat_payer: next }, { headers: authHeaders() });
      toast.success(next ? 'Отмечен как плательщик НДС' : 'Снят НДС');
      if (c.has_contract) {
        await axios.post(`${API}/api/admin/contracts/generate/${c.user_id}`, {}, { headers: authHeaders() });
      }
      await fetchClients(false);
    } catch {
      toast.error('Ошибка');
      await fetchClients(false);
    } finally {
      setBusyVat(prev => { const n = { ...prev }; delete n[c.user_id]; return n; });
    }
  };

  const generate = async (c) => {
    try {
      await axios.post(`${API}/api/admin/contracts/generate/${c.user_id}`, {}, { headers: authHeaders() });
      toast.success('Договор сформирован');
      fetchClients(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка генерации');
    }
  };

  const markSent = async (contractId) => {
    try {
      const r = await axios.post(`${API}/api/admin/contracts/${contractId}/send`, {}, { headers: authHeaders() });
      toast.success(r.data.notified ? 'Отправлен · уведомление ушло в чат саппорта' : 'Отмечен как отправленный');
      fetchClients(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  const openPdf = async (contractId, download = false) => {
    try {
      const r = await axios.get(`${API}/api/admin/contracts/${contractId}/pdf?download=${download}`,
        { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = `contract_${contractId}.pdf`; a.click();
      } else {
        window.open(url, '_blank');
      }
    } catch {
      toast.error('Не удалось открыть PDF');
    }
  };

  const filtered = clients.filter(c => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (c.company_name || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s)
      || (c.tax_id || '').includes(s);
  });

  const stats = {
    total: clients.length,
    points: clients.reduce((s, c) => s + (c.branch_count || 0), 0),
    signed: clients.filter(c => c.is_signed).length,
    ready: clients.filter(c => c.status === 'ready').length,
    none: clients.filter(c => !c.has_contract).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-[1280px] mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/ru/admin')} data-testid="contracts-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Назад
          </Button>
          <div className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-sky-700" />
            <h1 className="text-xl font-bold">Договоры B2B</h1>
          </div>
        </div>
      </header>

      <div className="max-w-[1280px] mx-auto px-4 py-6 space-y-4">
        <div className="flex gap-2">
          {[['clients', 'Клиенты и договоры'], ['templates', 'Шаблоны договоров']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`contracts-tab-${k}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${tab === k ? 'bg-sky-700 text-white' : 'bg-white border hover:bg-gray-50'}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === 'clients' ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[['Юрлиц (B2B)', stats.total, 'text-gray-900', `+${stats.points} ${pointsWord(stats.points)} сети`], ['Готовы', stats.ready, 'text-emerald-700'],
                ['Подписаны', stats.signed, 'text-green-700'], ['Без договора', stats.none, 'text-amber-700']].map(([l, v, c, sub]) => (
                <Card key={l}><CardContent className="p-4">
                  <div className="text-xs text-muted-foreground">{l}</div>
                  <div className={`text-2xl font-bold ${c}`}>{v}</div>
                  {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
                </CardContent></Card>
              ))}
            </div>

            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск по компании, email, БИН"
                  className="pl-9" data-testid="contracts-search" />
              </div>
              <Button onClick={syncBitrixAll} disabled={bzAll} variant="outline"
                data-testid="bitrix-sync-all" className="border-sky-300 text-sky-700 whitespace-nowrap">
                {bzAll ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
                {bzAll ? 'Синхронизация…' : 'Синхр. все с Bitrix'}
              </Button>
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Компания</th>
                      <th className="px-4 py-3">НДС</th>
                      <th className="px-4 py-3">Поставщик</th>
                      <th className="px-4 py-3">Статус</th>
                      <th className="px-4 py-3">Подписан</th>
                      <th className="px-4 py-3 text-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Загрузка...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Ничего не найдено</td></tr>
                    ) : filtered.map(c => (
                      <React.Fragment key={c.user_id}>
                      <tr className="border-t hover:bg-gray-50" data-testid={`contract-row-${c.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            {c.branch_count > 0 ? (
                              <button type="button" onClick={() => toggleExpand(c.user_id)}
                                data-testid={`branches-toggle-${c.user_id}`}
                                className="mt-0.5 text-gray-400 hover:text-sky-700 transition">
                                {expanded[c.user_id]
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />}
                              </button>
                            ) : <span className="w-4 inline-block" />}
                            <div>
                              <div className="font-medium flex items-center gap-2">
                                {c.company_name || '—'}
                                {c.branch_count > 0 && (
                                  <button type="button" onClick={() => toggleExpand(c.user_id)}
                                    className="text-[11px] font-medium text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5 hover:bg-sky-100 transition"
                                    data-testid={`branches-count-${c.user_id}`}>
                                    {c.branch_count} {pointsWord(c.branch_count)} сети
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-400">{c.email}{c.tax_id ? ` · БИН ${c.tax_id}` : ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Switch checked={c.is_vat_payer} onCheckedChange={() => toggleVat(c)}
                              disabled={!!busyVat[c.user_id]} data-testid={`vat-toggle-${c.user_id}`} />
                            {busyVat[c.user_id] && <RefreshCw className="w-3.5 h-3.5 text-sky-500 animate-spin" />}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600">
                          {busyVat[c.user_id] ? <span className="text-sky-500">обновление…</span> : (c.supplier || '—')}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge label={c.status_label} />
                          {c.missing_count > 0 && (
                            <div className="text-[11px] text-amber-600 mt-1" data-testid={`missing-${c.user_id}`}>
                              <div className="flex items-center gap-1 font-medium">
                                <AlertTriangle className="w-3 h-3" /> не хватает {c.missing_count} {c.missing_count === 1 ? 'поля' : 'полей'} — нужно дополнить:
                              </div>
                              <div className="text-amber-500 mt-0.5">{(c.missing_labels || []).join(', ')}</div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {c.is_signed
                            ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 justify-end">
                            {!c.has_contract ? (
                              <Button size="sm" variant="outline" onClick={() => generate(c)}
                                data-testid={`generate-${c.user_id}`} className="border-sky-300 text-sky-700">
                                <FileSignature className="w-3 h-3 mr-1" /> Создать
                              </Button>
                            ) : (
                              <>
                                {c.status === 'ready' && (
                                  <Button size="sm" variant="outline" onClick={() => markSent(c.contract_id)}
                                    data-testid={`mark-sent-${c.user_id}`} className="border-sky-300 text-sky-700">
                                    <Send className="w-3 h-3 mr-1" /> Отправить
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => setDetail({ contractId: c.contract_id })}
                                  data-testid={`open-${c.user_id}`} title="Открыть">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {c.is_signed && c.signed_scan_url && (
                                  <Button size="sm" variant="ghost" onClick={() => window.open(c.signed_scan_url, '_blank')}
                                    data-testid={`view-signed-${c.user_id}`} title="Открыть подписанный скан">
                                    <FileSignature className="w-4 h-4 text-green-600" />
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => openPdf(c.contract_id, true)}
                                  data-testid={`download-${c.user_id}`} title="Скачать сгенерированный PDF (шаблон)">
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => syncBitrix(c)}
                                  disabled={!!bzBusy[c.user_id]} data-testid={`bitrix-${c.user_id}`}
                                  title={c.bitrix_synced ? 'Пересинхронизировать с Bitrix' : 'Отправить в Bitrix'}>
                                  {bzBusy[c.user_id]
                                    ? <RefreshCw className="w-4 h-4 animate-spin text-sky-500" />
                                    : <Share2 className={`w-4 h-4 ${c.bitrix_synced ? 'text-green-600' : 'text-gray-500'}`} />}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {c.branch_count > 0 && expanded[c.user_id] && (
                        <tr className="bg-sky-50/40" data-testid={`branches-row-${c.user_id}`}>
                          <td colSpan={6} className="px-4 py-2">
                            <div className="pl-6 text-xs text-gray-600">
                              <div className="font-medium text-gray-500 mb-1">
                                Точки сети ({c.branch_count}) — входят в один договор юрлица:
                              </div>
                              <ul className="space-y-1">
                                {c.branches.map((b, i) => (
                                  <li key={i} className="flex items-center gap-2" data-testid={`branch-${c.user_id}-${i}`}>
                                    <MapPin className="w-3 h-3 text-sky-500 shrink-0" />
                                    <span className="font-medium text-gray-700">{b.name || '—'}</span>
                                    {b.point && b.point !== b.name && <span className="text-gray-400">· {b.point}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        ) : (
          <ContractTemplatesEditor />
        )}
      </div>

      {detail && (
        <ContractDetailModal
          contractId={detail.contractId}
          onClose={() => setDetail(null)}
          onChanged={() => fetchClients(false)}
          openPdf={openPdf}
        />
      )}
    </div>
  );
}

function ContractDetailModal({ contractId, onClose, onChanged, openPdf }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/api/admin/contracts/${contractId}`, { headers: authHeaders() });
    setData(r.data);
    setForm({ ...(r.data.buyer_data || {}), contract_number: r.data.contract_number || '', contract_date: r.data.contract_date || '' });
  }, [contractId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/contracts/${contractId}`, form, { headers: authHeaders() });
      toast.success('Сохранено');
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const uploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/api/admin/contracts/${contractId}/signed-scan`, fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Скан загружен, статус «Подписан»');
      await load();
      onChanged();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setUploading(false);
    }
  };

  const missing = new Set(data?.missing_fields || []);

  const markSentInModal = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/contracts/${contractId}/send`, {}, { headers: authHeaders() });
      toast.success(r.data.notified ? 'Отправлен · уведомление ушло в чат саппорта' : 'Отмечен как отправленный');
      await load();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="contract-detail-modal" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-sky-700" /> Договор
            {data && <StatusBadge label={data.status_label} />}
          </DialogTitle>
          <DialogDescription>Реквизиты покупателя для договора. Незаполненные обязательные поля подсвечены.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 text-center text-gray-400">Загрузка...</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Номер договора</label>
                <Input value={form.contract_number || ''} data-testid="field-contract_number"
                  onChange={e => setForm({ ...form, contract_number: e.target.value })}
                  placeholder="007/01/26"
                  className={missing.has('contract_number') ? 'border-amber-400' : ''} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Дата договора</label>
                <Input value={form.contract_date || ''} data-testid="field-contract_date"
                  onChange={e => setForm({ ...form, contract_date: e.target.value })}
                  placeholder="05 января 2026 г." />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(data.field_labels || []).map(f => (
                <div key={f.key}>
                  <label className="text-xs text-muted-foreground">
                    {f.label}{missing.has(f.key) && <span className="text-amber-600"> *нужно</span>}
                  </label>
                  <Input value={form[f.key] || ''} data-testid={`field-${f.key}`}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    className={missing.has(f.key) ? 'border-amber-400 bg-amber-50/40' : ''} />
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button onClick={save} disabled={saving} data-testid="contract-save" className="bg-emerald-600 hover:bg-emerald-700">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Сохраняю...' : 'Сохранить'}
              </Button>
              <Button variant="outline" onClick={() => openPdf(contractId, false)} data-testid="contract-view-pdf">
                <Eye className="w-4 h-4 mr-1" /> Посмотреть PDF
              </Button>
              <Button variant="outline" onClick={() => openPdf(contractId, true)} data-testid="contract-download-pdf">
                <Download className="w-4 h-4 mr-1" /> Скачать шаблон PDF
              </Button>
              {data.signed_scan_url && (
                <Button onClick={() => window.open(data.signed_scan_url, '_blank')}
                  data-testid="contract-open-signed" className="bg-green-600 hover:bg-green-700">
                  <FileSignature className="w-4 h-4 mr-1" /> Открыть подписанный
                </Button>
              )}
              {data.status === 'ready' && (
                <Button onClick={markSentInModal} data-testid="contract-mark-sent" className="bg-sky-600 hover:bg-sky-700">
                  <Send className="w-4 h-4 mr-1" /> Отметить отправленным
                </Button>
              )}
              <label className="inline-flex">
                <input type="file" accept="application/pdf,image/*" className="hidden" onChange={uploadScan} data-testid="contract-scan-input" />
                <span className={`inline-flex items-center px-3 py-2 text-sm rounded-md border cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-60' : ''}`}>
                  <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Загрузка...' : 'Загрузить скан подписанного'}
                </span>
              </label>
            </div>
            {(data.sent_at || data.signed_at) && (
              <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                {data.sent_at && <span>📤 Отправлен: {new Date(data.sent_at).toLocaleDateString('ru-RU')}</span>}
                {data.signed_at && <span className="text-green-700">✅ Подписан: {new Date(data.signed_at).toLocaleDateString('ru-RU')}</span>}
              </div>
            )}
            {data.signed_scan_url && (
              <a href={data.signed_scan_url} target="_blank" rel="noreferrer"
                className="text-xs text-green-700 underline flex items-center gap-1" data-testid="signed-scan-link">
                <CheckCircle2 className="w-3.5 h-3.5" /> Подписанный скан загружен — открыть
              </a>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="contract-detail-close">Закрыть</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContractTemplatesEditor() {
  const [list, setList] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('edit');

  useEffect(() => {
    axios.get(`${API}/api/admin/contract-templates`, { headers: authHeaders() })
      .then(r => {
        setList(r.data.templates || []);
        if (r.data.templates?.length) setActiveSlug(r.data.templates[0].slug);
      })
      .catch(() => toast.error('Не удалось загрузить шаблоны'));
  }, []);

  useEffect(() => {
    if (!activeSlug) return;
    axios.get(`${API}/api/admin/contract-templates/${activeSlug}`, { headers: authHeaders() })
      .then(r => setTpl(r.data)).catch(() => toast.error('Ошибка загрузки шаблона'));
  }, [activeSlug]);

  const save = async () => {
    if (!tpl?.content_html?.trim()) { toast.error('Шаблон пуст'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/contract-templates/${activeSlug}`,
        { content_html: tpl.content_html, title: tpl.title }, { headers: authHeaders() });
      toast.success('Шаблон сохранён');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {list.map(t => (
          <button key={t.slug} type="button" onClick={() => setActiveSlug(t.slug)}
            data-testid={`tpl-tab-${t.slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeSlug === t.slug ? 'bg-sky-700 text-white' : 'bg-white border hover:bg-gray-50'}`}>
            {t.is_vat ? 'НДС' : 'без НДС'} · {t.supplier_name}
          </button>
        ))}
      </div>
      {tpl && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-700" />
                <span className="font-medium text-sm">{tpl.title}</span>
                <Badge variant="outline">{tpl.version}</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant={mode === 'edit' ? 'default' : 'outline'} onClick={() => setMode('edit')}>Редактор</Button>
                <Button size="sm" variant={mode === 'preview' ? 'default' : 'outline'} onClick={() => setMode('preview')}>Предпросмотр</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="tpl-save" className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="w-3 h-3 mr-1" /> {saving ? '...' : 'Сохранить'}
                </Button>
              </div>
            </div>
            <div className="text-xs bg-sky-50 border border-sky-100 rounded p-2 text-sky-800">
              Плейсхолдеры (подставляются авто): {(tpl.placeholders || []).map(p => `{{${p.key}}}`).join('  ')}
            </div>
            {mode === 'edit' ? (
              <textarea value={tpl.content_html}
                onChange={e => setTpl({ ...tpl, content_html: e.target.value })}
                className="w-full font-mono text-xs border rounded-lg p-3 min-h-[500px]"
                data-testid="tpl-html-textarea" />
            ) : (
              <article className="bg-white rounded-lg border p-6 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(tpl.content_html) }} data-testid="tpl-preview" />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
